import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Folder,
  MoreHorizontal,
  Paperclip,
  Plus,
  Trash2
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/Button';
import { FolderSettingsModal } from '../../features/activity/FolderSettingsModal';
import {
  createConversationInFolderApi,
  fetchFolderApi,
  removeItemFromFolderApi,
  updateFolderFilesApi
} from '../../api/folders.api';
import { uploadMediaApi } from '../../api/media.api';
import { ACCEPTED_CHAT_FILE_ACCEPT, isAcceptedChatFile } from '../../constants/acceptedChatFiles';
import { useI18n } from '../../hooks/useI18n';
import { notifyRecentActivityUpsert } from '../../features/activity/recentActivityEvents';
import { classNames } from '../../utils/classNames';

const fileLabel = (file) => file?.name || file?.fileName || file?.title || file?.url || 'file';

export const FolderDetailPage = () => {
  const { folderId } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [folder, setFolder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creatingChat, setCreatingChat] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tab, setTab] = useState('chats');

  const reload = useCallback(async () => {
    if (!folderId) return;
    setLoading(true);
    setError('');
    try {
      const next = await fetchFolderApi(folderId);
      setFolder(next);
    } catch (err) {
      setError(err.message || t('folderCreateError'));
      setFolder(null);
    } finally {
      setLoading(false);
    }
  }, [folderId, t]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleNewChat = async () => {
    if (!folder?.id) return;
    setCreatingChat(true);
    try {
      const created = await createConversationInFolderApi(folder.id);
      notifyRecentActivityUpsert({
        id: created.id,
        type: 'conversation',
        title: created.title || '',
        updatedAt: created.updatedAt || new Date().toISOString(),
        isPinned: Boolean(created.isPinned),
        isArchived: Boolean(created.isArchived),
        folderId: created.folderId || folder.id,
        isTemporary: Boolean(created.isTemporary)
      });
      navigate(`/chat/${created.id}`);
    } catch (err) {
      setError(err.message || t('folderCreateError'));
    } finally {
      setCreatingChat(false);
    }
  };

  const handleUploadFiles = async (fileList) => {
    if (!folder?.id || !fileList?.length) return;
    const selected = Array.from(fileList);
    const accepted = selected.filter(isAcceptedChatFile);
    if (!accepted.length) {
      setError(t('folderFilesUnsupportedType'));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    setError(accepted.length < selected.length ? t('folderFilesUnsupportedType') : '');
    try {
      const uploaded = [];
      for (const file of accepted) {
        const result = await uploadMediaApi(file);
        uploaded.push({
          name: result.fileName || file.name,
          url: result.url,
          objectKey: result.objectKey,
          contentType: result.contentType || file.type
        });
      }
      const nextFiles = [...(folder.files || []), ...uploaded];
      const updated = await updateFolderFilesApi(folder.id, nextFiles);
      setFolder((prev) => (prev ? { ...prev, ...updated, conversations: prev.conversations } : updated));
      setTab('sources');
    } catch (err) {
      setError(err.message || t('folderFilesUploadError'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = async (index) => {
    if (!folder?.id) return;
    const nextFiles = (folder.files || []).filter((_, i) => i !== index);
    try {
      const updated = await updateFolderFilesApi(folder.id, nextFiles);
      setFolder((prev) => (prev ? { ...prev, ...updated, conversations: prev.conversations } : updated));
    } catch (err) {
      setError(err.message || t('folderFilesUploadError'));
    }
  };

  const handleRemoveConversation = async (conversationId) => {
    if (!folder?.id) return;
    try {
      await removeItemFromFolderApi(folder.id, {
        itemType: 'conversation',
        itemId: conversationId
      });
      await reload();
    } catch (err) {
      setError(err.message || t('folderCreateError'));
    }
  };

  const conversations = folder?.conversations || [];
  const files = folder?.files || [];
  const folderColor = folder?.color || '#f97316';

  return (
    <DashboardLayout flush title={folder?.name || t('folders')}>
      <section className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col px-4 py-6 sm:px-6">
        {loading ? (
          <div className="h-40 animate-pulse rounded-2xl bg-neutral-100 dark:bg-neutral-900" />
        ) : !folder ? (
          <p className="text-sm text-red-500">{error || t('folderEmpty')}</p>
        ) : (
          <>
            <div className="mb-6 flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <span
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-neutral-100 dark:bg-neutral-900"
                  style={{ color: folderColor }}
                >
                  <Folder size={20} />
                </span>
                <div className="min-w-0">
                  <h1 className="truncate text-2xl font-semibold tracking-tight text-neutral-950 dark:text-white">
                    {folder.name}
                  </h1>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSettingsOpen(true)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-neutral-200 text-neutral-600 transition hover:bg-neutral-50 dark:border-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-900"
                aria-label={t('folderSettings')}
              >
                <MoreHorizontal size={18} />
              </button>
            </div>

            <button
              type="button"
              onClick={handleNewChat}
              disabled={creatingChat}
              className="mb-6 flex w-full items-center gap-3 rounded-full border border-neutral-200 bg-white px-4 py-3.5 text-start text-sm text-neutral-500 shadow-sm transition hover:bg-neutral-50 disabled:opacity-60 dark:border-neutral-800 dark:bg-neutral-950 dark:hover:bg-neutral-900"
            >
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-neutral-100 text-neutral-700 dark:bg-neutral-900 dark:text-neutral-200">
                <Plus size={16} />
              </span>
              <span className="flex-1 truncate font-medium text-neutral-700 dark:text-neutral-200">
                {t('folderNewChatIn', { name: folder.name })}
              </span>
            </button>

            {error ? <p className="mb-4 text-sm text-red-500">{error}</p> : null}

            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-1 rounded-full bg-neutral-100 p-1 dark:bg-neutral-900">
                {[
                  { id: 'chats', label: t('folderChats') },
                  { id: 'sources', label: t('folderSources') }
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setTab(item.id)}
                    className={classNames(
                      'rounded-full px-3.5 py-1.5 text-sm font-medium transition',
                      tab === item.id
                        ? 'bg-white text-neutral-950 shadow-sm dark:bg-neutral-800 dark:text-white'
                        : 'text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'
                    )}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <div className="hidden items-center gap-2 text-xs text-neutral-400 sm:flex">
                <span>{t('folderSortNewest')}</span>
                <span>·</span>
                <span>{t('folderFilterAll')}</span>
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_CHAT_FILE_ACCEPT}
              className="hidden"
              onChange={(event) => handleUploadFiles(event.target.files)}
            />

            {tab === 'chats' ? (
              conversations.length === 0 ? (
                <div className="flex flex-1 flex-col items-center justify-center rounded-2xl border border-dashed border-neutral-200 px-6 py-16 text-center dark:border-neutral-800">
                  <p className="text-sm text-neutral-500">{t('folderChatsEmpty')}</p>
                  <Button className="mt-4" onClick={handleNewChat} disabled={creatingChat}>
                    <Plus size={16} />
                    {t('folderNewChat')}
                  </Button>
                </div>
              ) : (
                <ul className="space-y-1">
                  {conversations.map((conversation) => (
                    <li
                      key={conversation.id}
                      className="group flex items-center gap-2 rounded-xl px-3 py-2.5 hover:bg-[#F5F5F5] dark:hover:bg-neutral-900"
                    >
                      <button
                        type="button"
                        onClick={() => navigate(`/chat/${conversation.id}`)}
                        className="min-w-0 flex-1 truncate text-start text-sm text-neutral-800 dark:text-neutral-200"
                      >
                        {conversation.title || t('newConversation')}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveConversation(conversation.id)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-400 opacity-0 transition hover:bg-neutral-200/70 hover:text-red-600 group-hover:opacity-100"
                        aria-label={t('folderRemoveFrom')}
                      >
                        <Trash2 size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              )
            ) : files.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center rounded-2xl border border-dashed border-neutral-200 px-6 py-16 text-center dark:border-neutral-800">
                <div className="mb-4 flex items-center gap-2 text-neutral-400">
                  <Paperclip size={18} />
                </div>
                <h2 className="text-base font-semibold text-neutral-900 dark:text-white">
                  {t('folderSourcesTitle')}
                </h2>
                <p className="mt-2 max-w-md text-sm text-neutral-500">{t('folderSourcesBody')}</p>
                <Button
                  className="mt-5"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? t('uploading') : t('folderAddFiles')}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    className="!h-8 !px-3 text-xs"
                    disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploading ? t('uploading') : t('folderAddFiles')}
                  </Button>
                </div>
                <ul className="space-y-2">
                  {files.map((file, index) => (
                    <li
                      key={`${fileLabel(file)}-${index}`}
                      className="flex items-center justify-between gap-2 rounded-xl border border-neutral-200 px-3 py-2.5 dark:border-neutral-800"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm text-neutral-800 dark:text-neutral-200">
                          {fileLabel(file)}
                        </p>
                        {file.url ? (
                          <a
                            href={file.url}
                            target="_blank"
                            rel="noreferrer"
                            className="truncate text-xs text-neutral-500 hover:underline"
                          >
                            {file.url}
                          </a>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(index)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-200/70 hover:text-red-600"
                        aria-label={t('delete')}
                      >
                        <Trash2 size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </section>

      <FolderSettingsModal
        open={settingsOpen}
        folder={folder}
        onClose={() => setSettingsOpen(false)}
        onSaved={(saved) => {
          setFolder((prev) => (prev ? { ...prev, ...saved, conversations: prev.conversations } : saved));
        }}
        onDeleted={() => navigate('/folders')}
      />
    </DashboardLayout>
  );
};
